import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { brokerApi } from "@/hooks/useBrokerApi";
import { BrokerSendOpportunityModal } from "@/components/brokerPortal/BrokerSendOpportunityModal";

const STATUSES = ["draft", "sent", "driver_selected", "carrier_authorization_pending", "ready_for_pickup", "in_transit", "delivered"];

export const BrokerLoadsTab = () => {
  const [loads, setLoads] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [responses, setResponses] = useState([]);

  const load = () => brokerApi.get("/loads").then(({ data }) => setLoads(data)).catch(() => setLoads([]));
  useEffect(() => { load(); }, []);

  const updateStatus = async (loadId, status) => {
    try {
      await brokerApi.patch(`/loads/${loadId}`, { status });
      toast.success("Load status updated.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update status.");
    }
  };

  const toggleResponses = async (loadId) => {
    if (expandedId === loadId) {
      setExpandedId(null);
      return;
    }
    const { data } = await brokerApi.get(`/loads/${loadId}/responses`);
    setResponses(data);
    setExpandedId(loadId);
  };

  const advanceResponse = async (responseId, status, loadId) => {
    try {
      await brokerApi.patch(`/offer-responses/${responseId}`, { status });
      const { data } = await brokerApi.get(`/loads/${loadId}/responses`);
      setResponses(data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update response.");
    }
  };

  if (!loads) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-rr-orange" /></div>;

  return (
    <div data-testid="broker-loads-tab">
      <h2 className="font-heading text-xl font-bold text-rr-navy mb-5">Broker Load List</h2>
      {loads.length === 0 && <p className="text-rr-muted text-sm">No loads yet — create one from Post Load.</p>}
      <div className="space-y-3">
        {loads.map((l) => (
          <div key={l.id} data-testid={`broker-load-row-${l.id}`} className="bg-white border border-rr-line rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-heading font-bold text-rr-navy">{l.origin} → {l.destination}</p>
                <p className="text-xs text-rr-muted mt-0.5">{l.equipment} · {l.responsible_motor_carrier}</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={l.status}
                  onChange={(e) => updateStatus(l.id, e.target.value)}
                  data-testid={`broker-load-status-select-${l.id}`}
                  className="border border-rr-line rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-rr-orange capitalize"
                >
                  {STATUSES.map((s) => (<option key={s} value={s}>{s.replace(/_/g, " ")}</option>))}
                </select>
                <button onClick={() => setSendTarget(l)} data-testid={`broker-load-send-button-${l.id}`} className="flex items-center gap-1.5 text-xs font-bold bg-rr-navy text-white px-3 py-1.5 rounded-full hover:bg-rr-orange transition-colors">
                  <Send size={12} /> Send
                </button>
                <button onClick={() => toggleResponses(l.id)} data-testid={`broker-load-responses-toggle-${l.id}`} className="text-xs font-bold text-rr-orange hover:underline">
                  Responses
                </button>
              </div>
            </div>
            {expandedId === l.id && (
              <div data-testid={`broker-load-responses-panel-${l.id}`} className="mt-4 pt-4 border-t border-rr-line space-y-2">
                {responses.length === 0 && <p className="text-xs text-rr-muted">No responses yet.</p>}
                {responses.map((r) => (
                  <div key={r.id} data-testid={`broker-offer-response-${r.id}`} className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-rr-navy">{r.driver_name || r.driver_id}</span>
                    <select
                      value={r.status}
                      onChange={(e) => advanceResponse(r.id, e.target.value, l.id)}
                      data-testid={`broker-offer-response-status-${r.id}`}
                      className="border border-rr-line rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:border-rr-orange capitalize"
                    >
                      {["offer_sent", "driver_accepted", "declined", "responsible_motor_carrier_confirmed", "carrier_authorization_complete", "driver_clearance_complete", "ready_to_operate"].map((s) => (
                        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  </div>
                ))}
                <p className="text-[11px] text-rr-muted pt-1">Demo: driver acceptance/clearance steps are simulated here until the in-app driver response flow ships.</p>
              </div>
            )}
          </div>
        ))}
      </div>
      <BrokerSendOpportunityModal load={sendTarget} onOpenChange={(open) => !open && setSendTarget(null)} onSent={load} />
    </div>
  );
};
